import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { PencilLine, Calculator, FileImage, Star } from 'lucide-react';
import { Scenery } from '@/components/scenery';
import { ClevyKeyboard } from '@/components/clevy-keyboard';
import { PracticeOverlay, PracticeStartButton } from '@/features/practice/practice-overlay';

export const Route = createFileRoute('/')({
  component: StartScreen,
});

function StartScreen() {
  const [practicing, setPracticing] = useState(false);

  return (
    <div className="relative min-h-screen overflow-hidden">
      <Scenery />

      {/* Top Bar */}
      <header className="relative z-10 flex h-16 items-center gap-3 border-b-2 border-primary-soft bg-white/85 px-6 backdrop-blur">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-2xl font-bold text-white shadow-[0_3px_0_rgba(0,0,0,0.1)]">
          A
        </div>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-primary">Schreibhilfe</h1>
          <p className="text-xs font-medium text-gray-500">VS Unken</p>
        </div>
      </header>

      {/* Hero + Mode Cards */}
      <main className="relative z-10 flex flex-col items-center px-4 pt-8 pb-4 sm:pt-12">
        <h2 className="mb-2 text-center text-4xl font-bold tracking-tight text-primary sm:text-5xl">
          Schreibhilfe
        </h2>
        <p className="mb-8 text-center text-base font-semibold text-blue-900/80 sm:text-lg">
          Bereit loszulegen?
        </p>

        <div className="grid w-full max-w-6xl grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4 md:gap-5">
          <ModeCard
            to="/schreiben"
            label="Schreiben"
            description="Texte tippen mit Wort-Hilfe"
            color="primary"
            icon={<PencilLine className="h-10 w-10" />}
          />
          <ModeCard
            to="/rechnen"
            label="Rechnen"
            description="Plus, Minus, Mal & Co"
            color="danger"
            icon={<Calculator className="h-10 w-10" />}
          />
          <ModeCard
            to="/arbeitsblatt"
            label="Arbeitsblatt"
            description="Foto vom Blatt & befüllen"
            color="success"
            icon={<FileImage className="h-10 w-10" />}
          />
          <ModeCard
            to="/lehrer"
            label="Lehrer-Bereich"
            description="Wörter & Einstellungen"
            color="warning"
            icon={<Star className="h-10 w-10" />}
          />
        </div>

        {!practicing && (
          <div className="mt-6">
            <PracticeStartButton onStart={() => setPracticing(true)} />
          </div>
        )}

        <div className="mt-6 w-full max-w-5xl">
          <ClevyKeyboard interactive />
        </div>
      </main>

      {practicing && <PracticeOverlay onClose={() => setPracticing(false)} />}
    </div>
  );
}

function ModeCard({
  to,
  label,
  description,
  color,
  icon,
}: {
  to: string;
  label: string;
  description: string;
  color: 'primary' | 'danger' | 'success' | 'warning';
  icon: React.ReactNode;
}) {
  const colorClasses = {
    primary: 'text-primary',
    danger: 'text-danger',
    success: 'text-success',
    warning: 'text-warning',
  } as const;
  const bgClasses = {
    primary: 'bg-primary',
    danger: 'bg-danger',
    success: 'bg-success',
    warning: 'bg-warning',
  } as const;

  return (
    <Link
      to={to}
      className="group relative flex min-h-[180px] flex-col items-center justify-center rounded-cloud bg-white px-4 py-7 text-center shadow-[0_10px_30px_rgba(37,84,199,0.18)] transition-transform hover:-translate-y-1 hover:shadow-[0_14px_36px_rgba(37,84,199,0.24)] active:translate-y-0"
    >
      <div
        className={`mb-3 flex h-16 w-16 items-center justify-center rounded-2xl text-white shadow-[0_4px_0_rgba(0,0,0,0.08)] ${bgClasses[color]}`}
      >
        {icon}
      </div>
      <div className={`text-lg font-bold ${colorClasses[color]}`}>{label}</div>
      <div className="mt-1 text-sm font-semibold text-gray-500">{description}</div>
    </Link>
  );
}
